import { forwardRef, useLayoutEffect, useRef, useState } from 'react';
import { WALLPAPER, WALLPAPER_EXTRA } from '../art';

/* The scene tile: a rounded wallpaper field with a shadow tinted in its own
   hue (page spec 4.3). The wallpaper layer is clipped to the radius; the tile
   itself is overflow: visible so corner cards may cross its edge.
   When an image is missing (or not loaded yet) the layer shows a gradient in
   the same hue, so the tile always looks finished.

   Wallpaper picks for the sections live HERE, in one map, so the lead can
   swap any of them in one line. Hue order follows the motion spec
   (w1 peach-gold, w2 mint, w3 sky, w4 violet, w5 coral). In use (owner
   2026-09-29: soft, blurred, and on only about half the tiles): S3 tile 1
   w2 (the mint Sandy liked), S3 tile 3 "sky" (the soft extra; the violet was
   too heavy), S6 "sky" cropped light (the pastel read lime; the peach before it was
   the loudest block on the page), F1 "violet" (round 2: the soft violet extra, a hue no
   neighbor uses). S3 tile 2 and Ask Buffy use look="site". */
export const WALLS = {
  w1: { src: WALLPAPER.w1 && WALLPAPER.w1.tile, hue: '#FFB547', soft: '#FFE3A3', shadow: 'rgba(190, 120, 10, .40)' },
  w2: { src: WALLPAPER.w2 && WALLPAPER.w2.tile, hue: '#3CCB8B', soft: '#C8F3DD', shadow: 'rgba(16, 120, 90, .40)' },
  w3: { src: WALLPAPER.w3 && WALLPAPER.w3.tile, hue: '#3B9CFF', soft: '#CBE6FF', shadow: 'rgba(37, 99, 235, .40)' },
  w4: { src: WALLPAPER.w4 && WALLPAPER.w4.tile, hue: '#7B61FF', soft: '#DCD5FF', shadow: 'rgba(91, 33, 182, .45)' },
  w5: { src: WALLPAPER.w5 && WALLPAPER.w5.tile, hue: '#FF7A8A', soft: '#FFD6DC', shadow: 'rgba(200, 40, 60, .40)' },
  sky: { src: WALLPAPER_EXTRA && WALLPAPER_EXTRA.sky, hue: '#8CC8F5', soft: '#D8ECFF', shadow: 'rgba(37, 99, 235, .28)' },
  pastel: { src: WALLPAPER_EXTRA && WALLPAPER_EXTRA.pastel, hue: '#BFE3A8', soft: '#FFF4C9', shadow: 'rgba(90, 100, 160, .26)' },
  violet: { src: WALLPAPER_EXTRA && WALLPAPER_EXTRA.violetWaves, hue: '#B58CF5', soft: '#E4D6FF', shadow: 'rgba(91, 33, 182, .30)' },
  lilac: { src: WALLPAPER_EXTRA && WALLPAPER_EXTRA.violetLight, hue: '#C9B2F2', soft: '#EBE0FB', shadow: 'rgba(91, 33, 182, .26)' },
};

/* look="site": the owner wants only about half the tiles on a wallpaper; the
   rest look like our product (white or #FAFAFA canvas, hairline borders), so
   no wallpaper layer is rendered at all. canvas: 'white' | 'gray'. */
const Tile = forwardRef(function Tile({ wall, look = 'wall', canvas = 'gray', near = true, pos, className = '', style, children, ...rest }, ref) {
  if (look === 'site') {
    return (
      <div ref={ref} className={`cxs-tile is-site is-${canvas} ${className}`} style={style} {...rest}>
        {children}
      </div>
    );
  }
  const w = WALLS[wall] || WALLS.w1;
  return (
    <div
      ref={ref}
      className={`cxs-tile ${className}`}
      style={{ '--tile-hue': w.hue, '--tile-soft': w.soft, '--tile-shadow': w.shadow, '--wall-pos': pos || '50% 50%', ...style }}
      {...rest}
    >
      <div className="cxs-tile-wall" aria-hidden="true">
        {w.src && near ? <img src={w.src} alt="" decoding="async" draggable="false" /> : null}
      </div>
      {children}
    </div>
  );
});

export default Tile;

/* A fixed-size artboard (the scene is designed at w x h CSS px) scaled to
   the width it is given, so a tile's composition never reflows. By default
   it never scales up (qa #17: at 1024 the scenes grew past the page copy); a
   wider tile centers it instead. `maxScale` lets it grow a little (the step
   tiles pass 1.15, final formatting #7: 14px scene text reaches 16px at the
   cap). Scene text is 13.5-14px, so it stays 12px+ down to x0.885 (768). */
export function Artboard({ w, h, maxScale = 1, className = '', children }) {
  const ref = useRef(null);
  const [box, setBox] = useState({ scale: 1, left: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const set = () => {
      const cw = el.clientWidth;
      if (!cw) return;
      const scale = Math.min(maxScale, cw / w);
      const left = Math.round((cw - w * scale) / 2);
      setBox((b) => (b.scale === scale && b.left === left ? b : { scale, left }));
    };
    set();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, [w, maxScale]);
  return (
    <div ref={ref} className={`cxs-artboard ${className}`} style={{ height: Math.round(h * box.scale) }}>
      <div className="cxs-artboard-in" style={{ width: w, height: h, transform: `translate3d(${box.left}px, 0, 0) scale(${box.scale})` }}>
        {children}
      </div>
    </div>
  );
}
